import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Form, Button, Spinner, Alert, Badge } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function ProductOffersListPage() {
    const [offers, setOffers] = useState([]);
    const [allOffers, setAllOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [statusFilter, setStatusFilter] = useState("Pending");

    const [showDrawer, setShowDrawer] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [offerStatus, setOfferStatus] = useState("Approved");
    const [offerReason, setOfferReason] = useState("");
    const [offerPrice, setOfferPrice] = useState("");
    
    // Chat reply state variables for admin
    const [replyMessage, setReplyMessage] = useState("");
    const [replyPrice, setReplyPrice] = useState("");
    const [sendingReply, setSendingReply] = useState(false);

    const [updating, setUpdating] = useState(false);
    const [drawerError, setDrawerError] = useState(null);

    // Ref to handle auto-scrolling to the latest message in chat
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (selectedOffer) {
            scrollToBottom();
        }
    }, [selectedOffer?.messages]);

    const tableHeaders = [
        "PRODUCT",
        "BUYER",
        "SELLING PRICE",
        "OFFER PRICE",
        "OFFER STATUS",
        "OFFER REASON",
        "ACTION",
    ];

    const fetchOffers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/product-offers`,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data?.success) {
                const data = response.data.data || [];
                setAllOffers(data);

                // If drawer is open, keep selectedOffer in sync with latest fetched data
                if (selectedOffer) {
                    const refreshedCurrent = data.find((o) => o.id === selectedOffer.id);
                    if (refreshedCurrent) {
                        setSelectedOffer(refreshedCurrent);
                    }
                }
            } else {
                setAllOffers([]);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch product offers.");
        } finally {
            setLoading(false);
        }
    }, [selectedOffer?.id]);

    useEffect(() => {
        fetchOffers();
    }, [fetchOffers]);

    useEffect(() => {
        const filtered = allOffers.filter(
            (item) => (item.offer_status || "Pending").toLowerCase() === statusFilter.toLowerCase()
        );
        setOffers(filtered);
    }, [statusFilter, allOffers]);

    const getCount = (status) => {
        return allOffers.filter(
            (item) => (item.offer_status || "Pending").toLowerCase() === status.toLowerCase()
        ).length;
    };

    const handleOpenDrawer = (offer) => {
        setSelectedOffer(offer);
        setOfferStatus(offer.offer_status || "Approved");
        setOfferReason(offer.offer_reason || "");
        setOfferPrice(offer.offer_price || "");
        setReplyMessage("");
        setReplyPrice("");
        setDrawerError(null);
        setShowDrawer(true);
    };

    const handleCloseDrawer = () => {
        setShowDrawer(false);
        setSelectedOffer(null);
        setDrawerError(null);
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!selectedOffer) return;

        setSendingReply(true);
        setDrawerError(null);

        try {
            const payload = {};
            if (replyMessage.trim()) payload.message = replyMessage.trim();
            if (replyPrice) payload.offered_price = Number(replyPrice);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/product-offers/${selectedOffer.id}/reply`,
                payload,
                { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );

            if (response.data?.success) {
                setReplyMessage("");
                setReplyPrice("");
                await fetchOffers();
            }
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData?.errors) {
                const firstKey = Object.keys(errorData.errors)[0];
                setDrawerError(errorData.errors[firstKey][0]);
            } else {
                setDrawerError(errorData?.message || "Failed to send reply.");
            }
        } finally {
            setSendingReply(false);
        }
    };

    const handleUpdateOffer = async (e) => {
        e.preventDefault();
        if (!selectedOffer) return;

        setUpdating(true);
        setDrawerError(null);

        try {
            const payload = {
                offer_status: offerStatus,
                offer_reason: offerReason,
            };
            if (offerPrice) {
                payload.offer_price = Number(offerPrice);
            }

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/product-offers/${selectedOffer.id}`,
                payload,
                { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );

            if (response.data?.success) {
                await fetchOffers();
                handleCloseDrawer();
            }
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData?.errors) {
                const firstKey = Object.keys(errorData.errors)[0];
                setDrawerError(errorData.errors[firstKey][0]);
            } else {
                setDrawerError(errorData?.message || "Failed to update status.");
            }
        } finally {
            setUpdating(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "Approved":
            case "Accepted":
                return <Badge bg="success" className="px-3 py-1.5 text-uppercase rounded-pill fw-semibold">Approved</Badge>;
            case "Rejected":
                return <Badge bg="danger" className="px-3 py-1.5 text-uppercase rounded-pill fw-semibold">Rejected</Badge>;
            default:
                return <Badge bg="warning" text="dark" className="px-3 py-1.5 text-uppercase rounded-pill fw-semibold">{status || "Pending"}</Badge>;
        }
    };

    const isSold = Boolean(selectedOffer?.product?.sold_on);

    return (
        <PageLayout>
            <Row className="g-4">
                <Col xl={12}>
                    <div className="bg-white p-4 rounded-3 shadow-sm border border-light-subtle d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h3 className="fw-bold text-dark m-0 fs-4">Product Offers</h3>
                        <ul className="list-unstyled d-flex align-items-center gap-2 m-0 small text-secondary">
                            <li><Link to="#" className="text-decoration-none text-primary">Home</Link></li>
                            <li className="text-muted">~</li>
                            <li><Link to="#" className="text-decoration-none text-primary">Products</Link></li>
                            <li className="text-muted">~</li>
                            <li className="fw-semibold text-secondary">Product Offers</li>
                        </ul>
                    </div>
                </Col>

                {/* Refined Modern Cards */}
                <Col xl={12}>
                    <Row className="g-3">
                        <Col md={4}>
                            <div
                                onClick={() => setStatusFilter("Pending")}
                                className="p-4 rounded-3 bg-white border shadow-sm position-relative cursor-pointer transition-all"
                                style={{
                                    borderLeft: "5px solid #ffb020 !important",
                                    cursor: "pointer",
                                    boxShadow: statusFilter === "Pending" ? "0 0.5rem 1rem rgba(255, 176, 32, 0.15)" : "",
                                    transform: statusFilter === "Pending" ? "translateY(-2px)" : "none"
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h2 className="fw-bold mb-1 fs-1 text-dark">
                                            {loading ? <Spinner animation="border" size="sm" /> : getCount("Pending")}
                                        </h2>
                                        <span className="fw-bold text-uppercase text-muted fs-7 tracking-wider">
                                            Pending Offers
                                        </span>
                                    </div>
                                    <div className="rounded-circle bg-warning bg-opacity-10 p-3 d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px" }}>
                                        <i className="bi bi-clock-history text-warning fs-4"></i>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col md={4}>
                            <div
                                onClick={() => setStatusFilter("Approved")}
                                className="p-4 rounded-3 bg-white border shadow-sm position-relative cursor-pointer transition-all"
                                style={{
                                    borderLeft: "5px solid #10b981 !important",
                                    cursor: "pointer",
                                    boxShadow: statusFilter === "Approved" ? "0 0.5rem 1rem rgba(16, 185, 129, 0.15)" : "",
                                    transform: statusFilter === "Approved" ? "translateY(-2px)" : "none"
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h2 className="fw-bold mb-1 fs-1 text-dark">
                                            {loading ? <Spinner animation="border" size="sm" /> : getCount("Approved")}
                                        </h2>
                                        <span className="fw-bold text-uppercase text-muted fs-7 tracking-wider">
                                            Approved Offers
                                        </span>
                                    </div>
                                    <div className="rounded-circle bg-success bg-opacity-10 p-3 d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px" }}>
                                        <i className="bi bi-check-circle text-success fs-4"></i>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col md={4}>
                            <div
                                onClick={() => setStatusFilter("Rejected")}
                                className="p-4 rounded-3 bg-white border shadow-sm position-relative cursor-pointer transition-all"
                                style={{
                                    borderLeft: "5px solid #ef4444 !important",
                                    cursor: "pointer",
                                    boxShadow: statusFilter === "Rejected" ? "0 0.5rem 1rem rgba(239, 68, 68, 0.15)" : "",
                                    transform: statusFilter === "Rejected" ? "translateY(-2px)" : "none"
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h2 className="fw-bold mb-1 fs-1 text-dark">
                                            {loading ? <Spinner animation="border" size="sm" /> : getCount("Rejected")}
                                        </h2>
                                        <span className="fw-bold text-uppercase text-muted fs-7 tracking-wider">
                                            Rejected Offers
                                        </span>
                                    </div>
                                    <div className="rounded-circle bg-danger bg-opacity-10 p-3 d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px" }}>
                                        <i className="bi bi-x-circle text-danger fs-4"></i>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>

                {/* Table Section */}
                <Col xl={12}>
                    <div className="bg-white rounded-3 shadow-sm border border-light-subtle overflow-hidden">
                        {error && (
                            <div className="p-3">
                                <Alert variant="danger" className="m-0 rounded-2">{error}</Alert>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted fw-semibold">Loading offers...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table align-middle text-center table-hover mb-0">
                                    <thead style={{ backgroundColor: "#2d75c8", color: "#fff" }}>
                                        <tr>
                                            {tableHeaders.map((header, index) => (
                                                <th key={index} className="py-3 px-3 border-0 text-uppercase fw-bold small text-nowrap">
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="border-top-0">
                                        {offers.length > 0 ? (
                                            offers.map((item) => (
                                                <tr key={item.id} className="border-bottom border-light-subtle">
                                                    <td className="py-3 px-3 text-start">
                                                        <div className="d-flex align-items-center gap-2">
                                                            {item.product?.card_img && (
                                                                <img
                                                                    src={item.product.card_img.startsWith('http') ? item.product.card_img : `${process.env.REACT_APP_IMG_URL}/${item.product.card_img}`}
                                                                    alt={item.product?.title || "Product"}
                                                                    className="rounded border"
                                                                    style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                                                />
                                                            )}
                                                            <div>
                                                                <span className="fw-bold text-dark d-block text-truncate" style={{ maxWidth: "200px" }}>
                                                                    {item.product?.title || "-"}
                                                                </span>
                                                                <small className="text-muted">
                                                                    Code: {item.product?.product_code || "-"}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-3 text-start text-nowrap">
                                                        <div className="fw-semibold text-dark">{item.buyer?.name || "-"}</div>
                                                        <small className="text-muted d-block">{item.buyer?.email || ""}</small>
                                                        <small className="text-muted d-block">{item.buyer?.phone || ""}</small>
                                                    </td>
                                                    <td className="py-3 px-3 fw-bold text-dark text-nowrap">
                                                        ${item.product?.selling_price ? Number(item.product.selling_price).toLocaleString() : "0.00"}
                                                    </td>
                                                    <td className="py-3 px-3 fw-bold text-primary text-nowrap">
                                                        ${item.offer_price ? Number(item.offer_price).toLocaleString() : "0.00"}
                                                    </td>
                                                    <td className="py-3 px-3 text-nowrap">
                                                        {getStatusBadge(item.offer_status)}
                                                    </td>
                                                    <td className="py-3 px-3 text-muted text-start text-truncate" style={{ maxWidth: "200px" }}>
                                                        {item.offer_reason || "-"}
                                                    </td>
                                                    <td className="py-3 px-3 text-nowrap">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="px-3 py-1 fw-bold text-uppercase rounded-2 shadow-sm"
                                                            onClick={() => handleOpenDrawer(item)}
                                                        >
                                                            Manage / Chat
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={tableHeaders.length} className="text-center py-5 text-muted fw-semibold">
                                                    No product offers found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Backdrop Overlay */}
            {showDrawer && (
                <div
                    onClick={handleCloseDrawer}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1040,
                        transition: "opacity 0.3s ease",
                    }}
                />
            )}

            {/* Right Side Half-Width Drawer Panel */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    right: showDrawer ? 0 : "-52%",
                    width: "50%",
                    maxWidth: "700px",
                    minWidth: "350px",
                    height: "100vh",
                    backgroundColor: "#ffffff",
                    zIndex: 1050,
                    boxShadow: "-5px 0 25px rgba(0, 0, 0, 0.15)",
                    transition: "right 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Drawer Header */}
                <div className="p-4 border-bottom d-flex justify-content-between align-items-center bg-light">
                    <div>
                        <h5 className="fw-bold text-dark m-0">Manage Offer & Chat History</h5>
                        <small className="text-muted">Review conversation and update offer status</small>
                    </div>
                    <Button
                        variant="link"
                        className="text-dark p-0 text-decoration-none fs-4 fw-bold"
                        onClick={handleCloseDrawer}
                        style={{ lineHeight: 1 }}
                    >
                        &times;
                    </Button>
                </div>

                {/* Drawer Body (Scrollable) */}
                <div className="p-4 flex-grow-1" style={{ overflowY: "auto" }}>
                    {drawerError && (
                        <Alert variant="danger" className="mb-3 rounded-2">{drawerError}</Alert>
                    )}

                    {selectedOffer && (
                        <>
                            {/* Buyer & Product Quick Overview */}
                            <div className="bg-light p-3 rounded-3 mb-3 border">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <h6 className="fw-bold text-dark mb-1">{selectedOffer.product?.title}</h6>
                                        <span className="text-muted small">
                                            Buyer: <strong>{selectedOffer.buyer?.name}</strong> ({selectedOffer.buyer?.email})
                                        </span>
                                    </div>
                                    <div>
                                        <span className="badge bg-primary fs-6">Current Offer: ${selectedOffer.offer_price}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Chat Messages Conversation Stream */}
                            <div className="mb-4">
                                <h6 className="fw-bold text-secondary fs-7 text-uppercase mb-2">Conversation History</h6>
                                <div 
                                    className="border rounded-3 p-3 bg-light"
                                    style={{ maxHeight: "250px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px" }}
                                >
                                    {selectedOffer.messages && selectedOffer.messages.length > 0 ? (
                                        selectedOffer.messages.map((msg) => {
                                            const isAdmin = msg.sender_type === "Admin";
                                            return (
                                                <div
                                                    key={msg.id}
                                                    style={{
                                                        padding: "10px 14px",
                                                        borderRadius: "8px",
                                                        background: isAdmin ? "#e2e8f0" : "#dbeafe",
                                                        alignSelf: isAdmin ? "flex-end" : "flex-start",
                                                        maxWidth: "85%",
                                                        border: `1px solid ${isAdmin ? "#cbd5e1" : "#bfdbfe"}`,
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center gap-3 mb-1" style={{ fontSize: "11px", color: "#64748b" }}>
                                                        <strong>{isAdmin ? "You (Admin)" : msg.sender_name || "Buyer"}</strong>
                                                        <span>{msg.created_at}</span>
                                                    </div>
                                                    {msg.offered_price && (
                                                        <div className="fw-bold text-success mb-1" style={{ fontSize: "12px" }}>
                                                            Offered Price: ${msg.offered_price}
                                                        </div>
                                                    )}
                                                    {msg.message && (
                                                        <p className="mb-0 text-dark" style={{ fontSize: "13px", wordBreak: "break-word" }}>
                                                            {msg.message}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="text-muted text-center small my-3">No messages exchanged yet.</p>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            </div>

                            {/* Admin Reply Form (Available if Pending & Product not sold) */}
                            {selectedOffer.offer_status === "Pending" && !isSold ? (
                                <div className="border rounded-3 p-3 mb-4 bg-white shadow-sm">
                                    <h6 className="fw-bold text-dark small text-uppercase mb-2">Send Reply / Counter-Offer</h6>
                                    <Form onSubmit={handleSendReply}>
                                        <Row className="g-2 mb-2">
                                            <Col md={12}>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    size="sm"
                                                    placeholder="Counter Offer Price (Optional)"
                                                    value={replyPrice}
                                                    onChange={(e) => setReplyPrice(e.target.value)}
                                                />
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-2">
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                size="sm"
                                                placeholder="Type your message to buyer..."
                                                value={replyMessage}
                                                onChange={(e) => setReplyMessage(e.target.value)}
                                            />
                                        </Form.Group>
                                        <div className="text-end">
                                            <Button
                                                type="submit"
                                                variant="dark"
                                                size="sm"
                                                className="text-uppercase fw-bold px-3 py-1"
                                                disabled={sendingReply}
                                            >
                                                {sendingReply ? <Spinner animation="border" size="sm" /> : "Send Reply"}
                                            </Button>
                                        </div>
                                    </Form>
                                </div>
                            ) : null}

                            {/* Final Status Action Form (Approve / Reject) */}
                            <Form onSubmit={handleUpdateOffer}>
                                <div className="border-top pt-3">
                                    <h6 className="fw-bold text-secondary fs-7 text-uppercase mb-3">Update Final Status</h6>
                                    
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold text-uppercase text-secondary small">
                                                    OFFER STATUS
                                                </Form.Label>
                                                <Form.Select
                                                    className="shadow-none border bg-light py-2 px-3 fs-6 rounded-3"
                                                    value={offerStatus}
                                                    onChange={(e) => setOfferStatus(e.target.value)}
                                                    required
                                                >
                                                    <option value="Approved">Approved</option>
                                                    <option value="Rejected">Rejected</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold text-uppercase text-secondary small">
                                                    FINAL OFFER PRICE ($)
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    className="shadow-none border bg-light py-2 px-3 fs-6 rounded-3"
                                                    placeholder="Final price..."
                                                    value={offerPrice}
                                                    onChange={(e) => setOfferPrice(e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mt-3">
                                        <Form.Label className="fw-bold text-uppercase text-secondary small">
                                            OFFER REASON / NOTE
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            className="shadow-none border bg-light p-3 fs-6 rounded-3"
                                            placeholder="Enter reason or note..."
                                            value={offerReason}
                                            onChange={(e) => setOfferReason(e.target.value)}
                                        />
                                    </Form.Group>
                                </div>

                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button
                                        variant="light"
                                        className="text-uppercase fw-bold border-0 bg-light text-secondary px-4 py-2 rounded-2"
                                        onClick={handleCloseDrawer}
                                        disabled={updating}
                                    >
                                        CANCEL
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="text-uppercase fw-bold px-4 py-2 rounded-2 shadow-sm"
                                        disabled={updating}
                                    >
                                        {updating ? <Spinner animation="border" size="sm" /> : "SAVE CHANGES"}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    )}
                </div>
            </div>
        </PageLayout>
    );
}